import React, { Dispatch, SetStateAction } from 'react';
import { toast } from 'sonner';
import Router from 'next/router';
import ViewSelector from '@/components/ui/scout/ViewSelector';
import Filters from '@/components/ui/scout/Filters';
import { Views } from '@/types/view';
import { Button } from '@/components/shadcn/button';
import { validateOrganizationFunded } from '@/api/organization';
import * as Toast from '@/components/ui/toast/Toast';
import { useScout } from '@/hooks/useScout';
import { FiltersState } from '@/types/filter';
import { ScoutLocation } from '@/types/location';
import { useMapCredits } from '@/hooks/useMapCredits';
import { BURST_PRICE } from '@/components/ui/scout/Scout';
import { useUser } from '@/hooks/useUser';

interface Props {
  page: Views;
  activeView: Views;
  setActiveView: Dispatch<SetStateAction<Views>>;
  filters: FiltersState;
  setFilters: Dispatch<SetStateAction<FiltersState>>;
  locations: ScoutLocation[];
  activeLocation?: ScoutLocation;
  setIsUploadModalOpen: Dispatch<SetStateAction<boolean>>;
  setIsConfirmationModalOpen: Dispatch<SetStateAction<boolean>>;
  setBurstModalOptions: Dispatch<
    SetStateAction<{
      isOpen: boolean;
      locationsIds: string[];
      successCallback: () => void;
    }>
  >;
  setActiveLocationId: Dispatch<SetStateAction<string | number | null>>;
}

export default function Header({
  page,
  activeView,
  setActiveView,
  filters,
  setFilters,
  locations,
  activeLocation,
  setIsUploadModalOpen,
  setIsConfirmationModalOpen,
  setBurstModalOptions,
  setActiveLocationId,
}: Props) {
  const {
    selectedLocationIds,
    setActiveFrameIndex,
    setActiveSequenceIndex,
    setSelectedLocationIds,
  } = useScout();
  const { user } = useUser();
  const { setCreditsModalOptions } = useMapCredits();

  const activeLocationIndex = activeLocation
    ? locations.indexOf(activeLocation)
    : null;

  const hideFilters =
    locations.length > 0 && activeLocation && activeView === Views.Location;

  const handleTryingToUpload = async () => {
    const response = await validateOrganizationFunded(
      user?.currentOrganization,
    );

    if (response.credits > 0) {
      setIsUploadModalOpen(true);
    } else {
      toast.custom(t => (
        <Toast.Default
          title="Map Credits Needed"
          description="Purchase map credits to continue using Scout."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
            {
              label: 'Purchase Credits',
              onClick: () => {
                setCreditsModalOptions({ isVisible: true });
                toast.dismiss(t);
              },
              variant: 'default',
              size: 'fw',
            },
          ]}
        />
      ));
    }
  };

  const handleAddBurst = async () => {
    const response = await validateOrganizationFunded(
      user?.currentOrganization,
    );

    if ('error' in response) {
      console.error(response.error);
      toast.custom(t => (
        <Toast.Default
          title="Try Again"
          description="Something went wrong, please try again."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
          ]}
        />
      ));
      return;
    }

    if (response.credits >= selectedLocationIds.length * BURST_PRICE) {
      setBurstModalOptions({
        isOpen: true,
        locationsIds: selectedLocationIds,
        successCallback: () => setSelectedLocationIds([]),
      });
    } else {
      toast.custom(t => (
        <Toast.Default
          title="Map Credits Needed"
          description="Purchase map credits to continue using Scout."
          buttons={[
            {
              label: 'Dismiss',
              onClick: () => toast.dismiss(t),
              variant: 'secondary',
              size: 'fw',
            },
            {
              label: 'Purchase Credits',
              onClick: () => {
                setCreditsModalOptions({ isVisible: true });
                toast.dismiss(t);
              },
              variant: 'default',
              size: 'fw',
            },
          ]}
        />
      ));
    }
  };

  return (
    <>
      <ViewSelector page={page} setActiveView={setActiveView} />
      <div className="mb-2 mt-4 flex flex-col justify-between gap-2 text-right lg:my-6 lg:flex-row">
        <div
          data-hide={hideFilters}
          className="order-2 data-[hide=true]:hidden lg:order-1">
          <Filters filters={filters} setFilters={setFilters} />
        </div>
        <div
          data-active-location={
            activeView === Views.Location && !!activeLocation
          }
          className="order-1 flex justify-start gap-2 data-[active-location=true]:justify-between lg:order-2 lg:w-full lg:justify-end">
          {activeView === Views.List && (
            <>
              <Button
                className="h-[38px]"
                variant={'outline'}
                disabled={selectedLocationIds.length < 1}
                onClick={handleAddBurst}>
                Add Burst
              </Button>
              <Button
                className="h-[38px]"
                variant={'outline'}
                disabled={selectedLocationIds.length < 1}
                onClick={() => setIsConfirmationModalOpen(true)}>
                Remove Location
              </Button>
            </>
          )}
          <Button className="h-[38px]" onClick={handleTryingToUpload}>
            Upload
          </Button>
          {activeView === Views.Location &&
          typeof activeLocationIndex === 'number' ? (
            <div className="space-x-2">
              <Button
                className="h-[38px]"
                variant="outline"
                size="sm"
                onClick={() => {
                  setActiveSequenceIndex(0);
                  setActiveFrameIndex({ value: 0 });

                  const desiredLocation =
                    locations[activeLocationIndex - 1]._id;
                  setActiveLocationId(desiredLocation);
                  Router.push(`/scout/location/${desiredLocation}`, undefined, {
                    shallow: true,
                  });
                }}
                disabled={activeLocationIndex === 0}>
                Previous
              </Button>
              <Button
                className="h-[38px]"
                variant="outline"
                size="sm"
                onClick={() => {
                  setActiveSequenceIndex(0);
                  setActiveFrameIndex({ value: 0 });

                  const desiredLocation =
                    locations[activeLocationIndex + 1]._id;
                  setActiveLocationId(desiredLocation);

                  Router.push(`/scout/location/${desiredLocation}`, undefined, {
                    shallow: true,
                  });
                }}
                disabled={activeLocationIndex === locations.length - 1}>
                Next
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
